class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    clearTimeout(this.closeTimer);
    this.closing = false;
    this.removeAttribute('hidden');
    this.removeAttribute('inert');
    window.semboniaScrollLock.acquire(this);
    document.documentElement.classList.add('sembonia-cart-open');
    this.querySelector('.drawer__inner')?.setAttribute('data-lenis-prevent', '');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    if (window.semboniaMobileBaseline?.instantDrawers) {
      clearTimeout(this.openTimer);
      this.openController?.abort();
      this.classList.add('animate', 'active');
      this.trapDrawerFocus();
      return;
    }
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      if (this.closing || this.hasAttribute('hidden')) return;
      // Commit the offscreen position after unhiding, before entering.
      this.querySelector('.drawer__inner')?.getBoundingClientRect();
      this.classList.add('animate', 'active');
    });

    this.openController?.abort();
    this.openController = new AbortController();
    this.addEventListener(
      'transitionend',
      (event) => {
        if (event.target !== this.querySelector('.drawer__inner') || event.propertyName !== 'transform') return;
        if (this.closing || !this.classList.contains('active')) return;
        this.openController?.abort();
        this.trapDrawerFocus();
      },
      { signal:this.openController.signal }
    );


  }

  trapDrawerFocus() {
    const container = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    if (container && focusElement) trapFocus(container, focusElement);
  }

  close() {
    if (this.closing) return;
    this.closing = true;
    clearTimeout(this.openTimer);
    this.openController?.abort();
    this.classList.remove('active');
    // Inert can blur the focused child, so clear our focus trap first.
    if (window.semboniaMobileBaseline?.active && this.contains(document.activeElement)) removeTrapFocus();
    this.setAttribute('inert', '');
    if (this.contains(document.activeElement)) removeTrapFocus();
    const finish = () => {
      this.closing = false;
      this.setAttribute('hidden', '');
      this.querySelector('.drawer__inner')?.removeAttribute('data-lenis-prevent');
      document.documentElement.classList.remove('sembonia-cart-open');
      window.semboniaScrollLock.release(this);
      if (!window.semboniaScrollLock.size) this.activeElement?.focus({preventScroll:true});
    };
    if (window.semboniaMobileBaseline?.instantDrawers) finish();
    else this.closeTimer = setTimeout(finish, 500);
  }

  disconnectedCallback() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.openController?.abort();
    removeTrapFocus();
    document.documentElement.classList.remove('sembonia-cart-open');
    window.semboniaScrollLock.release(this);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
